import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import { DataCollectionEmailPreferences } from '../../../data-models/preferences-v3.data-model';
import { schemaToFormFields } from '../../../util/schema-utils';
import { createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { ConfigStateChip } from '../components/ConfigStateChip';
import { DetailsConfigActions } from '../DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../DataDisplay/ExitConfigBlocker';
import { generalSettingsSchema } from './dataCollectionEmailConfig.schema';
import { EmailTemplateSection } from './EmailTemplateSection';
import { useDataCollectionConfigFormState } from './useDataCollectionConfigFormState';

const sectionCss = css`
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 8px;
  padding: 1rem;
`;

export function DataCollectionConfig() {
  const { editStatus, methods, onSave, onReset } = useDataCollectionConfigFormState();

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' padding='8px 12px 12px' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <Stack direction='row' alignItems={'start'} columnGap='0.5rem'>
          <Typography variant='h4'>{`${getRouteTitle(ROUTES.ADMIN_PANEL_DATA_COLLECTION)}`}</Typography>
          <ConfigStateChip configStatus={editStatus} />
        </Stack>
        <Stack gap='1.5rem' marginBlock='2rem'>
          <GeneralSettingsSection />
          <SenderSettingsSection />
          <DisplaySettingsSection />
          <EmailTemplateSection title='Request Email Template' type='request' />
          <EmailTemplateSection title='Reminder Email Template' type='reminder' />
        </Stack>
        <DetailsConfigActions
          save={{ handler: onSave, disabled: editStatus !== 'modified' }}
          reset={{ handler: onReset }}
        />
      </Stack>
    </FormProvider>
  );
}

function GeneralSettingsSection() {
  const schema = generalSettingsSchema();
  const fields = schemaToFormFields(schema);
  const form = createFormFromFieldsArray(fields);
  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>General Settings</Typography>
      <FormFactoryWithStandardLayout form={form} />
    </Stack>
  );
}

function SenderSettingsSection() {
  const {
    control,
    formState: { errors },
  } = useFormContext<DataCollectionEmailPreferences>();

  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>Sender Settings</Typography>
      <Stack direction='row' gap='1rem'>
        <Controller
          name='senderEmail'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label='Sender Email'
              error={!!errors.senderEmail}
              helperText={errors.senderEmail?.message}
              fullWidth
              size='small'
            />
          )}
        />
        <Controller
          name='logoUrl'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label='Logo URL'
              error={!!errors.logoUrl}
              helperText={errors.logoUrl?.message}
              fullWidth
              size='small'
            />
          )}
        />
      </Stack>
    </Stack>
  );
}

function DisplaySettingsSection() {
  const {
    control,
    formState: { errors },
  } = useFormContext<DataCollectionEmailPreferences>();

  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>Display Settings</Typography>
      <Stack direction='row' gap='1rem'>
        <Controller
          name='primaryColor'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label='Primary Color'
              placeholder='#FF8200'
              error={!!errors.primaryColor}
              helperText={errors.primaryColor?.message ?? 'Hex color code (e.g., #FF8200)'}
              fullWidth
              size='small'
            />
          )}
        />
        <Controller
          name='secondaryColor'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label='Secondary Color'
              placeholder='#000000'
              error={!!errors.secondaryColor}
              helperText={errors.secondaryColor?.message ?? 'Hex color code (e.g., #000000)'}
              fullWidth
              size='small'
            />
          )}
        />
      </Stack>
      <Stack direction='row' gap='2rem'>
        <Controller
          name='showHeader'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value ?? false} />}
              label='Show Header'
            />
          )}
        />
        <Controller
          name='showPendingRequestsCount'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value ?? false} />}
              label='Show Pending Requests Count'
            />
          )}
        />
      </Stack>
    </Stack>
  );
}
